import type { GifProfileName } from '../constants';
import { ConversionError } from '../lib/errors';
import type {
  GifProgressPhase,
  GifWorkerRequest,
  GifWorkerResponse,
  GifWorkerSuccess,
} from './protocol';

export type AnimatedConversionResult = Omit<GifWorkerSuccess, 'type' | 'buffer'> & { blob: Blob };

export class AnimatedGifConverter {
  #worker: Worker | undefined;

  cancel(): void {
    this.#worker?.terminate();
    this.#worker = undefined;
  }

  async convert(
    file: File,
    profile: GifProfileName,
    onProgress: (phase: GifProgressPhase, percent: number) => void,
    signal?: AbortSignal,
  ): Promise<AnimatedConversionResult> {
    this.cancel();
    if (signal?.aborted) throw new ConversionError('cancelled', 'Conversion cancelled.');

    const buffer = await file.arrayBuffer();
    if (signal?.aborted) throw new ConversionError('cancelled', 'Conversion cancelled.');
    const worker = new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });
    this.#worker = worker;

    return new Promise((resolve, reject) => {
      const abort = (): void => {
        worker.terminate();
        if (this.#worker === worker) this.#worker = undefined;
        reject(new ConversionError('cancelled', 'Conversion cancelled.'));
      };
      signal?.addEventListener('abort', abort, { once: true });

      const finish = (): void => {
        signal?.removeEventListener('abort', abort);
        worker.terminate();
        if (this.#worker === worker) this.#worker = undefined;
      };

      worker.addEventListener('error', () => {
        finish();
        reject(
          new ConversionError('encode-failed', 'The animated converter stopped unexpectedly.'),
        );
      });
      worker.addEventListener('message', (event: MessageEvent<GifWorkerResponse>) => {
        const response = event.data;
        if (response.type === 'progress') {
          onProgress(response.phase, response.percent);
          return;
        }
        finish();
        if (response.type === 'error') {
          reject(
            new ConversionError(
              response.code === 'invalid-file' ? 'invalid-file' : 'encode-failed',
              response.message,
            ),
          );
          return;
        }
        resolve({
          blob: new Blob([response.buffer], { type: 'image/webp' }),
          sourceDimensions: response.sourceDimensions,
          outputDimensions: response.outputDimensions,
          sourceFrames: response.sourceFrames,
          outputFrames: response.outputFrames,
          durationMs: response.durationMs,
          animated: response.animated,
        });
      });

      const request: GifWorkerRequest = { type: 'convert', buffer, profile };
      worker.postMessage(request, [buffer]);
    });
  }
}
